import React from 'react';
import PropTypes from 'prop-types';
import MobileModal from './MobileModal';

// Strategy Comparison Component
// Compare Maximum Sustainable Income (MSI) across all 4 withdrawal strategies

const StrategyComparison = ({ inputs, msiResults, historicalResults, mcResults, onClose }) => {
    const strategies = [
        { id: 'Fixed', name: 'Fixed Sustainable', color: '#3b82f6', description: 'Constant inflation-adjusted withdrawal' },
        { id: 'DynamicSWR', name: 'Dynamic SWR', color: '#10b981', description: 'Adjusts based on current balance' },
        { id: 'VanguardGuardrails', name: 'Vanguard Guardrails', color: '#8b5cf6', description: 'Adjustment bands (±20%)' },
        { id: 'FloorAndCeiling', name: 'Floor & Ceiling', color: '#f59e0b', description: 'Min/max withdrawal limits' }
    ];

    // helpers
    const fmt = (n) => Math.round(n || 0).toLocaleString();

    // Find best strategy based on MSI
    const getBestStrategy = () => {
        if (!msiResults) return null;
        let best = { strategy: null, msi: 0 };
        
        Object.entries(msiResults).forEach(([strategy, msi]) => {
            if (msi > best.msi) {
                best = { strategy, msi };
            }
        });
        
        return best.strategy;
    };

    const bestStrategy = getBestStrategy();

    return (
        <MobileModal 
            isOpen={true}
            onClose={onClose}
            title="Strategy Comparison - Maximum Sustainable Income"
            size="large"
        >
            <div>
                    {!msiResults ? (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">⏳</div>
                            <h3 className="text-xl font-bold mb-2">No Results Available</h3>
                            <p className="text-gray-600">
                                Please run the retirement analysis first.
                            </p>
                        </div>
                    ) : (
                        <div>
                            {/* Explanation Banner */}
                            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
                                <h4 className="font-bold text-blue-900 mb-2">📖 Understanding Maximum Sustainable Income (MSI)</h4>
                                <div className="text-sm text-blue-800 space-y-2">
                                    <p><strong>MSI</strong> is the highest annual income each strategy can sustain over your {inputs.periodLength || 35}-year retirement without running out of money, based on historical market data from 1928-2023.</p>
                                    <p><strong>Higher MSI = More income</strong> you can safely withdraw each year while maintaining portfolio longevity.</p>
                                    <p>Each strategy was tested across 95+ years of historical market scenarios to find its maximum sustainable withdrawal level.</p>
                                </div>
                            </div>

                            {/* MSI Comparison Table */}
                            <div className="overflow-x-auto mb-6">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="border p-3 text-left font-bold">Strategy</th>
                                            <th className="border p-3 text-left font-bold">Description</th>
                                            <th className="border p-3 text-right font-bold">Max Sustainable Income</th>
                                            <th className="border p-3 text-center font-bold">vs Best</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {strategies.map(strategy => {
                                            const msi = msiResults[strategy.id] || 0;
                                            const isBest = strategy.id === bestStrategy;
                                            const bestMsi = msiResults[bestStrategy] || 1;
                                            const percentOfBest = bestMsi > 0 ? ((msi / bestMsi) * 100).toFixed(1) : 0;
                                            
                                            return (
                                                <tr key={strategy.id} className={isBest ? 'bg-green-50 font-semibold' : 'hover:bg-gray-50'}>
                                                    <td className="border p-3">
                                                        <div className="flex items-center gap-2">
                                                            <div style={{ width: '12px', height: '12px', backgroundColor: strategy.color, borderRadius: '3px' }}></div>
                                                            {strategy.name}
                                                            {isBest && <span className="text-xs bg-green-600 text-white px-2 py-1 rounded ml-2">HIGHEST</span>}
                                                        </div>
                                                    </td>
                                                    <td className="border p-3 text-sm text-gray-600">{strategy.description}</td>
                                                    <td className="border p-3 text-right text-lg">
                                                        <span className={isBest ? 'text-green-600 font-bold' : ''}>
                                                            ${fmt(msi)}/year
                                                        </span>
                                                    </td>
                                                    <td className="border p-3 text-center">
                                                        <span className={percentOfBest >= 95 ? 'text-green-600' : percentOfBest >= 85 ? 'text-yellow-600' : 'text-red-600'}>
                                                            {percentOfBest}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Income Difference Analysis */}
                            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
                                <h4 className="font-bold text-yellow-900 mb-3">💰 Income Differences</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-yellow-800 mb-2"><strong>Highest Income:</strong></p>
                                        <p className="text-2xl font-bold text-yellow-900">
                                            ${fmt(msiResults[bestStrategy])}/year
                                        </p>
                                        <p className="text-sm text-yellow-700">{strategies.find(s => s.id === bestStrategy)?.name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-yellow-800 mb-2"><strong>Income Range:</strong></p>
                                        <p className="text-lg font-bold text-yellow-900">
                                            ${fmt(Math.min(...Object.values(msiResults)))} - ${fmt(Math.max(...Object.values(msiResults)))}
                                        </p>
                                        <p className="text-sm text-yellow-700">
                                            ${fmt(Math.max(...Object.values(msiResults)) - Math.min(...Object.values(msiResults)))}/year difference
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* What This Means */}
                            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
                                <h4 className="font-bold text-blue-900 mb-2">🎯 What This Means For You</h4>
                                <p className="text-sm text-blue-800 mb-3">
                                    Choosing <strong>{strategies.find(s => s.id === bestStrategy)?.name}</strong> gives you the highest sustainable income 
                                    of <strong>${fmt(msiResults[bestStrategy])}/year</strong>.
                                </p>
                                <p className="text-sm text-blue-800">
                                    The lowest strategy provides ${fmt(Math.min(...Object.values(msiResults)))}/year - 
                                    that's ${fmt((msiResults[bestStrategy] || 0) - Math.min(...Object.values(msiResults)) )}/year less income over your retirement.
                                </p>
                            </div>

                            {/* Detailed Monte Carlo Note */}
                            <div className="bg-gray-50 border border-gray-300 p-4 rounded-lg mb-6">
                                <h4 className="font-bold text-gray-900 mb-2">ℹ️ For Detailed Analysis</h4>
                                <p className="text-sm text-gray-700">
                                    The MSI values above are calculated using rolling period analysis across all historical data. 
                                    To see detailed Monte Carlo simulation results (success rates, percentiles, year-by-year projections) for a specific strategy, 
                                    select that strategy in the main calculator and click "Run my plan".
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-center gap-3">
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
            </div>
        </MobileModal>
    );
};

StrategyComparison.propTypes = {
    inputs: PropTypes.object.isRequired,
    msiResults: PropTypes.object,
    historicalResults: PropTypes.object,
    mcResults: PropTypes.object,
    onClose: PropTypes.func.isRequired
};

export default StrategyComparison;
